import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Player } from '@lottiefiles/react-lottie-player';
import { useAuth } from '../../context/AuthContext';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const { resetPassword } = useAuth();

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsValidEmail(validateEmail(value));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await resetPassword(email);
      setSuccess(true);
      // Show confetti animation
      showConfetti();
    } catch (err) {
      setError('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const showConfetti = () => {
    const colors = ['#6366f1', '#ec4899', '#3b82f6', '#10b981'];
    const count = 200;

    for (let i = 0; i < count; i++) {
      createParticle(colors[Math.floor(Math.random() * colors.length)]);
    }
  };

  const createParticle = (color) => {
    const particle = document.createElement('particle');
    document.body.appendChild(particle);

    const x = window.innerWidth / 2;
    const y = window.innerHeight / 2;
    const destinationX = (Math.random() - 0.5) * 300;
    const destinationY = (Math.random() - 0.5) * 300;
    const rotation = Math.random() * 520;
    const delay = Math.random() * 200;

    particle.style.backgroundImage = `radial-gradient(circle, ${color} 0%, transparent 80%)`;
    particle.style.width = '10px';
    particle.style.height = '10px';
    particle.style.position = 'fixed';
    particle.style.left = `${x}px`;
    particle.style.top = `${y}px`;
    particle.style.transform = `translate(-50%, -50%)`;
    particle.style.borderRadius = '50%';
    particle.style.pointerEvents = 'none';

    const animation = particle.animate([
      {
        transform: `translate(-50%, -50%) translate(0, 0) rotate(0deg)`,
        opacity: 1
      },
      {
        transform: `translate(-50%, -50%) translate(${destinationX}px, ${destinationY}px) rotate(${rotation}deg)`,
        opacity: 0
      }
    ], {
      duration: 1000 + delay,
      easing: 'cubic-bezier(0, .9, .57, 1)',
      delay: delay
    });

    animation.onfinish = () => particle.remove();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 to-pink-500 relative overflow-hidden">
      {/* Background animation */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
          className="absolute inset-0 bg-gradient-to-br from-indigo-500/30 to-pink-500/30 blur-3xl"
        />
      </div>

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <div className="backdrop-blur-lg bg-white/80 dark:bg-gray-900/80 p-8 rounded-xl shadow-2xl space-y-8">
          <div className="text-center">
            <Player
              autoplay
              loop
              src="https://assets10.lottiefiles.com/packages/lf20_xlmz9xwm.json"
              style={{ height: '150px', width: '150px', margin: '0 auto' }}
            />
            <motion.h2
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              className="mt-2 text-3xl font-bold text-gray-900 dark:text-white"
            >
              Reset Password
            </motion.h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="rounded-lg bg-red-50 dark:bg-red-900/30 p-4"
                >
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                        {error}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="relative">
              <motion.div
                initial={false}
                animate={email ? { y: -20, scale: 0.85 } : { y: 0, scale: 1 }}
                className="absolute left-3 top-2 text-gray-500 pointer-events-none origin-left"
              >
                Email address
              </motion.div>
              <motion.input
                whileFocus={{ scale: 1.01 }}
                type="email"
                value={email}
                onChange={handleEmailChange}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-gray-900 dark:text-white bg-transparent focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
              />
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: isValidEmail ? 1 : 0 }}
                className="absolute right-3 top-2"
              >
                <svg
                  className="w-6 h-6 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </motion.div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading || !isValidEmail}
              className="relative w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-pink-500 hover:from-indigo-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                />
              ) : success ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex items-center space-x-2"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>Check your email</span>
                </motion.div>
              ) : (
                'Reset Password'
              )}
            </motion.button>

            <div className="text-center">
              <Link
                to="/login"
                className="group inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                <motion.svg
                  whileHover={{ x: -4 }}
                  className="mr-2 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </motion.svg>
                Back to login
              </Link>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
} 